// Canonical meal-detail routing lives under a SINGLE user-facing route: /meal-view/:mealId.
// Grouped / multi-food / rebuild-backed (AI photo, basket) meals need the specialized grouped renderer
// (authoritative rebuild provenance, metadata-only edit, rebuild-aware Log Again) which is preserved
// verbatim in GroupedMealView; ordinary meals use the standard MealView body. This pure predicate
// decides which one to mount, from the URL (?view=grouped) or router location.state.grouped.
export const GROUPED_VIEW_PARAM = 'view';
export const GROUPED_VIEW_VALUE = 'grouped';

export function wantsGroupedView(search, state) {
  try {
    const sp = new URLSearchParams(search || '');
    if (sp.get(GROUPED_VIEW_PARAM) === GROUPED_VIEW_VALUE) return true;
  } catch (_e) { /* ignore malformed search */ }
  return !!(state && state.grouped === true);
}

// Append the grouped marker to an existing query string (callers building /meal-view links for grouped
// meals). Keeps any params already present; never duplicates the marker.
export function withGroupedParam(queryString) {
  const sp = new URLSearchParams(queryString || '');
  sp.set(GROUPED_VIEW_PARAM, GROUPED_VIEW_VALUE);
  return sp.toString();
}

// Canonical target for a legacy /custom-meal/:mealId deep link. Those links were always grouped, so we
// redirect to /meal-view/:mealId with the grouped marker, preserving any existing query params.
export function legacyCustomMealTarget(mealId, search) {
  return `/meal-view/${mealId}?${withGroupedParam(search)}`;
}

// A meal_id-only heuristic for "this is a grouped/rebuild-backed meal", for
// callers (e.g. Favorites) that only have a lightweight summary — no plan
// row (plan.source / plan.ingredient_count) and no full meal document
// (data.rebuild_v2 / ingredient .rebuilt markers) to check. Covers every
// backend-generated grouped meal_id prefix: 'ailog_group_' (legacy AI-log
// commit, backend/services/ai_food/commit.py), 'ailog_rebuild_' (current
// rebuild-v2 commit, backend/services/ai_food/rebuild_commit.py — this is
// also the example legacyCustomMealTarget's own test uses), and 'grouped_'
// (the prefix Home.jsx already checks for). So a favorited grouped meal
// opens the same canonical grouped detail experience as it does everywhere
// else it's opened from (see AGENTS.md pre-launch audit, finding MEALS-3).
export function looksLikeGroupedMealId(mealId) {
  return typeof mealId === 'string' && (
    mealId.startsWith('ailog_group_') ||
    mealId.startsWith('ailog_rebuild_') ||
    mealId.startsWith('grouped_')
  );
}
